import { skipToken } from "@reduxjs/toolkit/query";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import { UserRole } from "../../constants/userRole";
import { useOptionalUserWithAccount } from "../../context/UserAccountProvider";
import {
  useGetClaimResearchRunQuery,
  useGetClaimResearchRunsQuery,
  useRequestClaimResearchMutation,
  useSaveClaimResearchVerdictMutation,
} from "../../store/queries/claimResearch";
import {
  IClaimResearchPolicy,
  IClaimResearchRun,
  IClaimResearchVerdict,
} from "../../types/claimResearch";
import {
  ALREADY_RUNNING_TEXT,
  apiErrorText,
  CLAIM_RESEARCH_ROUTES,
  CLAIM_RESEARCH_VERDICTS,
  contractTextLine,
  DISABLED_TEXT,
  formatCost,
  formatDollars,
  formatElapsed,
  formatRunDate,
  isActive,
  isDisabledError,
  isStuck,
  isUnpicked,
  NO_RUNS_TEXT,
  NOTE_MAX_CHARS,
  POLL_MS,
  queuedFor,
  QUESTION_LABEL,
  QUEUED_TEXT,
  QUEUED_UNPICKED_TEXT,
  ROUTE_LABELS,
  routeLabel,
  RERUN_BUTTON_LABEL,
  RUN_BUTTON_LABEL,
  runningFor,
  RUNNING_TEXT,
  runPickerLabel,
  safeHttpUrl,
  STAFF_REVIEW_NOTE,
  STAFF_REVIEW_TITLE,
  STUCK_TEXT,
  TICK_MS,
  VERDICT_LABELS,
  verdictFormError,
  verdictLabel,
  verdictSeverity,
} from "./independentAnalysis";

/**
 * CLE-2965 — "AI Insights v2": an AI research agent's analysis of one claim (SuperAdmin-only tab).
 *
 * A run is an agent that reads the claim, its contract and the payer's published policies, and
 * writes up what happened — a verdict, the payer's arithmetic, the policies it relied on and a
 * letter. It takes 2–5 minutes, costs about $1 and changes nothing on the claim. Every run is
 * kept; a reviewer grades each one (agree / disagree + the correct label), which is how the team
 * decides whether to trust it. The existing AI Insights tab is untouched, so the two can be
 * compared side by side.
 *
 * Layout (Mike, 2026-10-29): a headline card (verdict, next step, confidence, summary), then
 * collapsed sections, then the staff review box — which is gated on SuperAdmin by its OWN check,
 * so it stays staff-only if the tab is ever opened to everyone. None of the paragraph above is
 * shown to the user: no spec text on screen, and never the word "independent".
 *
 * 🚨 Every string in a run's result is model output and renders as TEXT — never as HTML, and a
 * policy URL is a link only when it is http(s), opened with rel="noopener noreferrer".
 */

const Wrapper = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Section = styled.section`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem 1.25rem;
`;

const SectionTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 15px;
  font-weight: 600;
  color: #495057;
`;

const Prose = styled.p`
  margin: 0;
  color: #212529;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const Summary = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.65;
  color: #1d1d1f;
  white-space: pre-wrap;
`;

const Muted = styled.span`
  color: #6c757d;
  font-size: 13px;
`;

const Quote = styled.blockquote`
  margin: 0.5rem 0;
  padding: 0.5rem 0.75rem;
  border-left: 3px solid #ced4da;
  background: #f8f9fa;
  color: #343a40;
  font-style: italic;
  white-space: pre-wrap;
`;

const LineTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: 0.4rem 0.5rem;
    border-bottom: 1px solid #e9ecef;
    vertical-align: top;
  }

  th {
    color: #6c757d;
    font-weight: 600;
  }

  td.num,
  th.num {
    text-align: right;
    white-space: nowrap;
  }
`;

const LetterText = styled.pre`
  margin: 0.75rem 0 0 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const BulletList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  line-height: 1.6;

  li {
    white-space: pre-wrap;
  }
`;

const RunDetails = styled.div`
  color: #6c757d;
  font-size: 12px;
`;

/** Staff-only: dashed and tinted so it never reads as part of the analysis. */
const StaffBox = styled.section`
  border: 2px dashed var(--orange-300, #fdba74);
  border-radius: 8px;
  background: var(--orange-50, #fff7ed);
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StaffNote = styled.p`
  margin: 0;
  color: #6c757d;
  font-size: 13px;
`;

const Question = styled.div`
  padding: 0.75rem 1rem;
  border-left: 3px solid var(--orange-400, #fb923c);
  background: #ffffff;
  border-radius: 4px;
  white-space: pre-wrap;
`;

const asArray = <T,>(value: T[] | null | undefined): T[] =>
  Array.isArray(value) ? value : [];

/** `Date.now()`, re-read every `intervalMs` while `intervalMs > 0` — so elapsed times tick. */
const useNow = (intervalMs: number) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (intervalMs <= 0) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
};

function Waiting({ run, now }: { run: IClaimResearchRun; now: number }) {
  const queued = run.status === "QUEUED";
  const stuck = !queued && isStuck(run, now);
  const elapsed = queued
    ? `queued ${formatElapsed(queuedFor(run, now))} ago`
    : `started ${formatElapsed(runningFor(run, now))} ago`;
  return (
    <Section data-testid="claimResearch.waiting">
      <div
        className="flex flex-column align-items-center text-center gap-2 py-2"
        data-testid="claimResearch.waitingColumn"
      >
        <span>{queued ? QUEUED_TEXT : RUNNING_TEXT}</span>
        <Muted>{elapsed}</Muted>
        {queued && isUnpicked(run, now) && (
          <p
            className="text-sm text-orange-700 m-0"
            data-testid="claimResearch.unpicked"
          >
            {QUEUED_UNPICKED_TEXT}
          </p>
        )}
        {stuck && (
          <p
            className="text-sm text-orange-700 m-0"
            data-testid="claimResearch.stuck"
          >
            {STUCK_TEXT}
          </p>
        )}
        {stuck ? (
          <i className="pi pi-exclamation-triangle text-orange-700 text-2xl mt-1" />
        ) : (
          <ProgressSpinner
            className="mt-1"
            style={{ width: "32px", height: "32px" }}
          />
        )}
      </div>
    </Section>
  );
}

function PolicyItem({ policy }: { policy: IClaimResearchPolicy }) {
  const href = safeHttpUrl(policy.url);
  const overrides =
    policy.contractOverrides === null || policy.contractOverrides === undefined
      ? null
      : String(policy.contractOverrides);
  return (
    <li className="mb-3" data-testid="claimResearch.policy">
      <div className="font-semibold">
        {policy.name || "Unnamed policy"}
        {policy.number && <Muted> · {policy.number}</Muted>}
      </div>
      {policy.versionCoversDOS === false && (
        <Tag
          severity="warning"
          className="mt-1"
          value="Edition after the service date"
          data-testid="claimResearch.policy.offVersion"
        />
      )}
      {policy.effectiveDate && (
        <div>
          <Muted>Effective: {policy.effectiveDate}</Muted>
        </div>
      )}
      {policy.quote && <Quote>{policy.quote}</Quote>}
      {policy.appliesBecause && (
        <Prose>
          <strong>Applies because: </strong>
          {policy.appliesBecause}
        </Prose>
      )}
      {overrides && (
        <Prose className="mt-1">
          <strong>Contract overrides it: </strong>
          {overrides}
          {policy.contractOverridesWhy && ` — ${policy.contractOverridesWhy}`}
        </Prose>
      )}
      {policy.url &&
        (href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm"
            data-testid="claimResearch.policy.link"
          >
            Open the policy <i className="pi pi-external-link text-xs" />
          </a>
        ) : (
          <Muted data-testid="claimResearch.policy.rawUrl">{policy.url}</Muted>
        ))}
    </li>
  );
}

type CopyState = "idle" | "copied" | "failed";
const COPY_LABEL: Record<CopyState, string> = {
  idle: "Copy",
  copied: "Copied",
  failed: "Copy failed",
};
const COPY_ICON: Record<CopyState, string> = {
  idle: "pi pi-copy",
  copied: "pi pi-check",
  failed: "pi pi-times",
};

function Letter({ text }: { text: string }) {
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);
  const show = (state: CopyState) => {
    setCopyState(state);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopyState("idle"), 2000);
  };
  const copy = async () => {
    try {
      if (!navigator.clipboard) throw new Error("No clipboard");
      await navigator.clipboard.writeText(text);
      show("copied");
    } catch {
      show("failed");
    }
  };
  return (
    <div data-testid="claimResearch.letter">
      <div className="flex justify-content-end">
        <Button
          type="button"
          label={COPY_LABEL[copyState]}
          icon={COPY_ICON[copyState]}
          className="p-button-outlined p-button-secondary p-button-sm"
          onClick={copy}
        />
      </div>
      <LetterText>{text}</LetterText>
    </div>
  );
}

/** "Payer policies (2)" — a count only where there is a list to count. */
const titled = (title: string, count?: number) =>
  count === undefined ? title : `${title} (${count})`;

function DoneRun({ run }: { run: IClaimResearchRun }) {
  const result = run.result ?? {};
  const verdict = run.verdict ?? result.verdict;
  const route = run.recoveryRoute ?? result.recoveryRoute;
  const confidence = run.confidence ?? result.realUnderpaymentConfidence;
  const lines = asArray(result.expectedVsActualByLine);
  const policies = asArray(result.policies);
  const terms = asArray(result.contractTermsRelied);
  const gaps = asArray(result.dataGaps);
  const textStatus = asArray(run.contractTextStatus);
  const unread = textStatus.filter((s) => s.status !== "read");
  const textLine = contractTextLine(run.contractTextStatus);
  const confidenceWhy = [
    result.realUnderpaymentConfidenceReason,
    result.confidence && `Verdict confidence: ${result.confidence}`,
  ]
    .filter(Boolean)
    .join(" ");
  const hasTerms = terms.length > 0 || textLine !== null;

  // AccordionTab must be a direct child of Accordion (it reads each child's type), so the
  // sections are written out here rather than in a wrapper component; `false` children are
  // skipped by Accordion.
  return (
    <div className="flex flex-column gap-3" data-testid="claimResearch.done">
      <Section data-testid="claimResearch.headline">
        <div
          className="flex flex-wrap align-items-center gap-3 mb-3"
          data-testid="claimResearch.headlineRow"
        >
          <Tag
            severity={verdictSeverity(verdict)}
            value={verdictLabel(verdict)}
            data-testid="claimResearch.verdict"
          />
          <span data-testid="claimResearch.route">
            <Muted>Next step: </Muted>
            <strong>{routeLabel(route)}</strong>
          </span>
          {confidence !== null && confidence !== undefined && (
            <span
              data-testid="claimResearch.confidence"
              title={confidenceWhy || undefined}
            >
              <Muted>Confidence: </Muted>
              <strong>{confidence}/100</strong>
              {confidenceWhy && (
                <i className="pi pi-info-circle text-xs ml-1 text-color-secondary" />
              )}
            </span>
          )}
          <span data-testid="claimResearch.ranAt">
            <Muted>AI analysis ran at: </Muted>
            {formatRunDate(run.createdAt)}
          </span>
        </div>
        {result.summary && (
          <Summary data-testid="claimResearch.summary">
            {result.summary}
          </Summary>
        )}
      </Section>

      <Accordion multiple data-testid="claimResearch.sections">
        {result.appealLetter && (
          <AccordionTab header="Appeal letter">
            <Letter text={result.appealLetter} />
          </AccordionTab>
        )}

        {lines.length > 0 && (
          <AccordionTab header={titled("Line-by-line", lines.length)}>
            <div style={{ overflowX: "auto" }}>
              <LineTable data-testid="claimResearch.lines">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th className="num">Billed</th>
                    <th className="num">Expected</th>
                    <th className="num">Payer allowed</th>
                    <th className="num">Difference</th>
                    <th>Adjustments</th>
                    <th>Explanation</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={`${line.line}-${index}`}>
                      <td>{line.line ?? "—"}</td>
                      <td className="num">{formatDollars(line.billed)}</td>
                      <td className="num">{formatDollars(line.expected)}</td>
                      <td className="num">
                        {formatDollars(line.payerAllowed)}
                      </td>
                      <td className="num">{formatDollars(line.diff)}</td>
                      <td>{line.adjustments ?? ""}</td>
                      <td>{line.explanation ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </LineTable>
            </div>
          </AccordionTab>
        )}

        {policies.length > 0 && (
          <AccordionTab header={titled("Payer policies", policies.length)}>
            <ul className="list-none p-0 m-0">
              {policies.map((policy, index) => (
                <PolicyItem
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${policy.number ?? policy.name}-${index}`}
                  policy={policy}
                />
              ))}
            </ul>
          </AccordionTab>
        )}

        {result.payerAmountReconstruction && (
          <AccordionTab header="How the payer's amount was calculated">
            <Prose>{result.payerAmountReconstruction}</Prose>
          </AccordionTab>
        )}

        {hasTerms && (
          <AccordionTab
            header={
              terms.length > 0
                ? titled("Contract terms used", terms.length)
                : "Contract terms used"
            }
          >
            {terms.length > 0 && (
              <BulletList>
                {terms.map((term, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={index}>{term}</li>
                ))}
              </BulletList>
            )}
            {textLine && (
              <div
                className={terms.length > 0 ? "mt-3" : undefined}
                data-testid="claimResearch.contractText"
              >
                <Muted>{textLine}</Muted>
                {unread.length > 0 && (
                  <BulletList className="text-sm mt-1">
                    {unread.map((doc) => (
                      <li key={doc.attachmentId}>
                        {doc.fileName}: {doc.reason ?? doc.status}
                      </li>
                    ))}
                  </BulletList>
                )}
              </div>
            )}
          </AccordionTab>
        )}

        {result.correctedClaimNote && (
          <AccordionTab header="Corrected claim">
            <Prose>{result.correctedClaimNote}</Prose>
          </AccordionTab>
        )}

        {gaps.length > 0 && (
          <AccordionTab header={titled("Caveats", gaps.length)}>
            <BulletList data-testid="claimResearch.gaps">
              {gaps.map((gap, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>{gap}</li>
              ))}
            </BulletList>
          </AccordionTab>
        )}
      </Accordion>
    </div>
  );
}

function OtherVerdict({ verdict }: { verdict: IClaimResearchVerdict }) {
  return (
    <li className="mb-2" data-testid="claimResearch.otherVerdict">
      <strong>{verdict.agrees ? "Agrees" : "Disagrees"}</strong>
      {!verdict.agrees && verdict.correctVerdict && (
        <> — {verdictLabel(verdict.correctVerdict)}</>
      )}
      {!verdict.agrees && verdict.correctRoute && (
        <> · {routeLabel(verdict.correctRoute)}</>
      )}
      <Muted>
        {" "}
        (reviewer {verdict.userId}, {formatRunDate(verdict.updatedAt)})
      </Muted>
      {verdict.note && <Prose className="text-sm">{verdict.note}</Prose>}
    </li>
  );
}

const VERDICT_OPTIONS = CLAIM_RESEARCH_VERDICTS.map((value) => ({
  value,
  label: VERDICT_LABELS[value],
}));
const ROUTE_OPTIONS = CLAIM_RESEARCH_ROUTES.map((value) => ({
  value,
  label: ROUTE_LABELS[value],
}));

function VerdictPanel({
  run,
  claimId,
  currentUserId,
}: {
  run: IClaimResearchRun;
  claimId: string;
  currentUserId: string | undefined;
}) {
  const verdicts = asArray(run.verdicts);
  const mine = verdicts.find((v) => v.userId === currentUserId) ?? null;
  const others = verdicts.filter((v) => v.userId !== currentUserId);
  const [agrees, setAgrees] = useState<boolean | null>(mine?.agrees ?? null);
  const [correctVerdict, setCorrectVerdict] = useState<string | null>(
    mine?.correctVerdict ?? null
  );
  const [correctRoute, setCorrectRoute] = useState<string | null>(
    mine?.correctRoute ?? null
  );
  const [note, setNote] = useState(mine?.note ?? "");
  const [save, { isLoading, isError, error }] =
    useSaveClaimResearchVerdictMutation();

  const formError = verdictFormError({ agrees, correctVerdict, note });

  const onSave = async () => {
    if (formError || agrees === null) return;
    try {
      await save({
        runId: run.id,
        claimId,
        body: {
          agrees,
          ...(agrees ? {} : { correctVerdict, correctRoute }),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      }).unwrap();
    } catch {
      // `isError` renders the message
    }
  };

  return (
    <div
      className="surface-0 border-round p-3"
      data-testid="claimResearch.verdictPanel"
    >
      <SectionTitle>Your verdict on this run</SectionTitle>
      {mine && (
        <p className="text-sm mt-0" data-testid="claimResearch.myVerdict">
          Saved: <strong>{mine.agrees ? "Agree" : "Disagree"}</strong>
          {!mine.agrees && mine.correctVerdict && (
            <> — {verdictLabel(mine.correctVerdict)}</>
          )}
          {!mine.agrees && mine.correctRoute && (
            <> · {routeLabel(mine.correctRoute)}</>
          )}
          <Muted> ({formatRunDate(mine.updatedAt)})</Muted>
        </p>
      )}
      <div className="flex gap-2 mb-3">
        <Button
          type="button"
          label="Agree"
          icon="pi pi-thumbs-up"
          aria-pressed={agrees === true}
          className={`p-button-sm ${
            agrees === true ? "p-button-success" : "p-button-outlined"
          }`}
          onClick={() => setAgrees(true)}
        />
        <Button
          type="button"
          label="Disagree"
          icon="pi pi-thumbs-down"
          aria-pressed={agrees === false}
          className={`p-button-sm ${
            agrees === false ? "p-button-danger" : "p-button-outlined"
          }`}
          onClick={() => setAgrees(false)}
        />
      </div>
      {agrees === false && (
        <div className="flex flex-wrap gap-3 mb-3">
          <div className="flex flex-column gap-1">
            <label htmlFor={`claimResearch.correctVerdict.${run.id}`}>
              Correct verdict
            </label>
            <Dropdown
              inputId={`claimResearch.correctVerdict.${run.id}`}
              value={correctVerdict}
              options={VERDICT_OPTIONS}
              onChange={(e) => setCorrectVerdict(e.value)}
              placeholder="Pick the correct verdict"
              style={{ minWidth: "16rem" }}
              data-testid="claimResearch.correctVerdict"
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor={`claimResearch.correctRoute.${run.id}`}>
              Correct next step (optional)
            </label>
            <Dropdown
              inputId={`claimResearch.correctRoute.${run.id}`}
              value={correctRoute}
              options={ROUTE_OPTIONS}
              onChange={(e) => setCorrectRoute(e.value)}
              placeholder="Pick the correct next step"
              showClear
              style={{ minWidth: "16rem" }}
              data-testid="claimResearch.correctRoute"
            />
          </div>
        </div>
      )}
      <div className="flex flex-column gap-1 mb-3">
        <label htmlFor={`claimResearch.note.${run.id}`}>Note (optional)</label>
        <InputTextarea
          id={`claimResearch.note.${run.id}`}
          value={note}
          rows={3}
          autoResize
          onChange={(e) => setNote(e.target.value)}
        />
        <div className="flex justify-content-between text-sm">
          <Muted>Don&apos;t include patient names or dates of birth.</Muted>
          <span
            className={
              note.length > NOTE_MAX_CHARS
                ? "text-red-600 font-semibold"
                : "text-color-secondary"
            }
            data-testid="claimResearch.noteCounter"
          >
            {note.length} / {NOTE_MAX_CHARS}
          </span>
        </div>
      </div>
      <div className="flex align-items-center gap-3">
        <Button
          type="button"
          label="Save verdict"
          icon="pi pi-save"
          className="p-button-sm"
          loading={isLoading}
          disabled={formError !== null}
          onClick={onSave}
        />
        {formError && agrees !== null && (
          <Muted data-testid="claimResearch.verdictFormError">
            {formError}
          </Muted>
        )}
      </div>
      {isError && (
        <Message
          severity="error"
          className="mt-2"
          text={`The verdict was not saved: ${
            apiErrorText(error) ?? "unknown error"
          }`}
        />
      )}
      {others.length > 0 && (
        <div className="mt-3">
          <SectionTitle>Other reviewers</SectionTitle>
          <ul className="list-none p-0 m-0">
            {others.map((v) => (
              <OtherVerdict key={v.id} verdict={v} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * ClearHealth staff only — gated on SuperAdmin here, not just by the tab, so the reviewer's
 * controls and the model's question to Ani never reach anyone else even if the tab opens up.
 */
function StaffReview({
  run,
  claimId,
  currentUserId,
}: {
  run: IClaimResearchRun;
  claimId: string;
  currentUserId: string | undefined;
}) {
  const userInfo = useOptionalUserWithAccount()?.userInfo;
  if (userInfo?.scope !== UserRole.SUPER_ADMIN) return null;

  const question = run.result?.questionForAni;
  const cost = formatCost(run.costUsd);
  return (
    <StaffBox data-testid="claimResearch.staffReview">
      <div>
        <SectionTitle className="mb-1">{STAFF_REVIEW_TITLE}</SectionTitle>
        <StaffNote>{STAFF_REVIEW_NOTE}</StaffNote>
      </div>
      {question && (
        <Question data-testid="claimResearch.question">
          <strong>{QUESTION_LABEL}: </strong>
          {question}
        </Question>
      )}
      <VerdictPanel
        key={run.id}
        run={run}
        claimId={claimId}
        currentUserId={currentUserId}
      />
      <RunDetails data-testid="claimResearch.runDetails">
        Prompt {run.promptVersion}
        {cost && ` · cost ${cost}`}
        {run.numTurns !== null &&
          run.numTurns !== undefined &&
          ` · ${run.numTurns} turns`}
        {` · ${asArray(run.toolLog).length} web calls`}
        {run.finishedAt && ` · finished ${formatRunDate(run.finishedAt)}`}
      </RunDetails>
    </StaffBox>
  );
}

interface IIndependentAnalysisTabProps {
  claimId: string;
  /** The signed-in SuperAdmin — tells their own verdict from other reviewers'. */
  currentUserId: string | undefined;
}

export function IndependentAnalysisTab({
  claimId,
  currentUserId,
}: IIndependentAnalysisTabProps) {
  const list = useGetClaimResearchRunsQuery(claimId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // The run the POST handed back — shown at once, before the list refetch lands.
  const [requested, setRequested] = useState<IClaimResearchRun | null>(null);
  const [alreadyRunning, setAlreadyRunning] = useState(false);
  const [requestError, setRequestError] = useState<unknown>(null);
  const [request, { isLoading: isRequesting }] =
    useRequestClaimResearchMutation();

  const listed = list.data ?? [];
  const runs = useMemo(
    () =>
      requested && !listed.some((r) => r.id === requested.id)
        ? [requested, ...listed]
        : listed,
    [listed, requested]
  );
  const selectedSummary =
    runs.find((r) => r.id === selectedId) ?? runs[0] ?? null;

  const byId = useGetClaimResearchRunQuery(selectedSummary?.id ?? skipToken, {
    pollingInterval: isActive(selectedSummary) ? POLL_MS : 0,
  });
  const run =
    byId.data && byId.data.id === selectedSummary?.id
      ? byId.data
      : selectedSummary;
  const active = isActive(run);
  const now = useNow(active ? TICK_MS : 0);

  // The polled run settled while the list still has it in flight: refresh the list (the picker
  // labels and the "is anything running" check read it).
  const { refetch } = list;
  const settledId = useRef<string | null>(null);
  useEffect(() => {
    if (
      run &&
      !isActive(run) &&
      isActive(listed.find((r) => r.id === run.id)) &&
      settledId.current !== run.id
    ) {
      settledId.current = run.id;
      refetch();
    }
  }, [run, listed, refetch]);

  // The picker shows the polled copy of the selected run, so its label moves with it.
  const pickerRuns = runs.map((r) => (run && r.id === run.id ? run : r));
  const anyActive = pickerRuns.some((r) => isActive(r));

  const onRun = async () => {
    setRequestError(null);
    setAlreadyRunning(false);
    try {
      const response = await request(claimId).unwrap();
      setRequested(response.data);
      setSelectedId(response.data.id);
      setAlreadyRunning(!response.created);
    } catch (error) {
      setRequestError(error);
    }
  };

  const runButton = (
    <Button
      type="button"
      label={runs.length > 0 ? RERUN_BUTTON_LABEL : RUN_BUTTON_LABEL}
      icon="pi pi-search"
      loading={isRequesting}
      disabled={anyActive}
      onClick={onRun}
    />
  );

  return (
    <Wrapper data-testid="claimResearch.tab">
      <div className="flex flex-wrap align-items-center gap-3">{runButton}</div>

      {requestError !== null &&
        (isDisabledError(requestError) ? (
          <Message
            severity="warn"
            text={DISABLED_TEXT}
            data-testid="claimResearch.disabled"
          />
        ) : (
          <Message
            severity="error"
            text={`The analysis could not be started: ${
              apiErrorText(requestError) ?? "unknown error"
            }`}
          />
        ))}

      {alreadyRunning && isActive(run) && (
        <Message
          severity="info"
          text={ALREADY_RUNNING_TEXT}
          data-testid="claimResearch.alreadyRunning"
        />
      )}

      {list.isLoading && (
        <div className="flex justify-content-center p-4">
          <ProgressSpinner style={{ width: "40px", height: "40px" }} />
        </div>
      )}

      {list.isError && (
        <Message
          severity="error"
          text={`The runs could not be loaded: ${
            apiErrorText(list.error) ?? "unknown error"
          }`}
        />
      )}

      {!list.isLoading && !list.isError && runs.length === 0 && (
        <Muted data-testid="claimResearch.noRuns">{NO_RUNS_TEXT}</Muted>
      )}

      {/* One run: nothing here — the headline already says when it ran. */}
      {pickerRuns.length > 1 && (
        <div className="flex align-items-center gap-2">
          <label htmlFor="claimResearch.runPicker" className="text-sm">
            Run:
          </label>
          <Dropdown
            inputId="claimResearch.runPicker"
            value={run?.id ?? null}
            options={pickerRuns.map((r) => ({
              value: r.id,
              label: runPickerLabel(r),
            }))}
            onChange={(e) => setSelectedId(e.value)}
            className="p-inputtext-sm"
            data-testid="claimResearch.runPicker"
          />
        </div>
      )}

      {run && isActive(run) && <Waiting run={run} now={now} />}

      {run?.status === "FAILED" && (
        <Section data-testid="claimResearch.failed">
          <Message
            severity="warn"
            text={`The analysis did not finish: ${
              run.error ?? "unknown error"
            }`}
          />
        </Section>
      )}

      {run?.status === "DONE" && (
        <>
          <DoneRun run={run} />
          <StaffReview
            key={run.id}
            run={run}
            claimId={claimId}
            currentUserId={currentUserId}
          />
        </>
      )}
    </Wrapper>
  );
}

export default IndependentAnalysisTab;
